import { FormattedDate } from "$components/date/formatted-date";
import { makeStyles } from "$components/mui-styles";
import { dosageCount } from "$routes/patient/_lib/styled-dosage-string";
import type { HistoricAncPrescription } from "$routes/patient/[:patientId]/antenatal-care/_lib/anc-encounters";
import { Grid, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { DispensationStrategy } from "elephant-web/gql";
import React from "react";
import { FormattedMessage } from "react-intl";
import AncPrescriptionLabel from "./anc-prescription-label";

const useStyles = makeStyles((theme) => ({
  subTitle: {
    fontFamily: theme.typography.fontBold,
    fontSize: 16,
    lineHeight: 1.47,
    letterSpacing: 0.3,
  },
  summary: {
    padding: 0,
  },
  marginBottom: {
    marginBottom: 5,
  },
  dispenseBtn: { maxWidth: "178px", float: "right" },
}));

type LoadedLocations = {
  locations: { _id: string; name: string }[];
  loading: false;
};

type LoadingLocations = {
  locations: undefined;
  loading: true;
};

type LocationsData = LoadedLocations | LoadingLocations;

const AncPrescriptionDetails: FC<{
  prescription: HistoricAncPrescription;
  prescribedBy: string;
  locationName: string;
  prescribedAt: Date;
  locationsData: LocationsData;
}> = ({
  prescription,
  prescribedBy,
  locationName,
  prescribedAt,
  locationsData,
}) => {
  const classes = useStyles();
  const dispensation = prescription.dispensations?.items[0];
  const dispensedLocationName =
    dispensation?.dispenseLocation?.name ||
    locationsData.locations?.find(
      ({ _id }) => _id === dispensation?.dispenseLocation?._id,
    )?.name ||
    "Unknown";

  const shouldShowTotalDispenseAmount =
    prescription.dosage?.unit === "tablet" ||
    prescription.dosage?.unit === "capsule";

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} className={classes.marginBottom}>
        <Typography className={classes.subTitle}>
          <AncPrescriptionLabel prescription={prescription} />
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="body2" component="p">
          <strong>
            <FormattedMessage
              id="patient.prescriptions.dispense.prescription.details.datePrescribed"
              defaultMessage="Date prescribed:"
            />
          </strong>
          &nbsp;
          <FormattedDate value={prescribedAt} />
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="body2" component="p">
          <strong>
            <FormattedMessage
              id="patient.prescriptions.dispense.prescription.prescribedBy"
              defaultMessage="Prescribed by:"
            />
          </strong>
          &nbsp;
          {prescribedBy}
        </Typography>
      </Grid>
      {prescription.dosage && (
        <>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" component="p">
              <strong>
                <FormattedMessage
                  id="patient.prescriptions.dispense.prescription.singleDosage"
                  defaultMessage="Single dosage:"
                />
                &nbsp;
              </strong>
              {prescription.dosage
                ? `${prescription.dosage.value} ${prescription.dosage.unit}`
                : null}
            </Typography>
          </Grid>
          {shouldShowTotalDispenseAmount && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" component="p">
                <strong>
                  <FormattedMessage
                    id="patient.history.prescription.dosesToDispense"
                    defaultMessage="Doses to dispense:"
                  />
                </strong>
                &nbsp;
                {dosageCount(
                  prescription?.rate,
                  prescription?.frequency,
                  prescription?.dosage,
                )}
              </Typography>
            </Grid>
          )}
        </>
      )}

      {locationName && (
        <Grid item xs={12}>
          <Typography variant="body2" component="p">
            <strong>
              <FormattedMessage
                id="patient.history.prescription.location"
                defaultMessage="Location:"
              />
            </strong>
            &nbsp;
            {locationName}
          </Typography>
        </Grid>
      )}

      {dispensation &&
        (dispensation.dispensationStrategy ===
          DispensationStrategy.ProvidedIntendedClinicalProduct ||
          dispensation.dispensationStrategy ===
            DispensationStrategy.ProvidedAlternativeClinicalProduct) && (
          <>
            {dispensation.alternativeDispensed && dispensation.sku && (
              <Grid item xs={12}>
                <Typography variant="body2" component="p">
                  <strong>
                    <FormattedMessage
                      id="patient.history.prescription.medicineDispensed"
                      defaultMessage="Medicine dispensed:"
                    />
                  </strong>
                  &nbsp;
                  {dispensation.sku.name}
                </Typography>
              </Grid>
            )}
            {dispensation.quantity !== undefined && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" component="p">
                  <strong>
                    <FormattedMessage
                      id="patient.history.prescription.quantityDispensed"
                      defaultMessage="Quantity dispensed:"
                    />
                  </strong>
                  &nbsp;
                  {`${dispensation.quantity} `}
                  <FormattedMessage
                    id="patient.history.prescription.units"
                    defaultMessage="units"
                  />
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              {locationsData.loading ? (
                <Skeleton
                  variant="text"
                  height={20}
                  width={150}
                  animation={"pulse"}
                  data-testid="dispense-location-skeleton"
                />
              ) : (
                <Typography variant="body2" component="p">
                  <strong>
                    <FormattedMessage
                      id="patient.history.prescription.dispenseLocation"
                      defaultMessage="Dispense Location:"
                    />
                  </strong>
                  &nbsp;
                  {dispensedLocationName}
                </Typography>
              )}
            </Grid>
          </>
        )}
    </Grid>
  );
};

const memoised = React.memo(AncPrescriptionDetails);

export { memoised as AncPrescriptionDetails };
